<template>
  <div class="separation-anxiety-exercise-card">
    <SeparationAnxietyInstructionsCard />
    <va-card v-if="!hasEmail"
      title="Info"
      class="exercise-customizations"
    >
      <div class="exercise-customizations">
        <va-input
          v-model="longestAbsenceString"
          type="string"
          aria-label="Longest comfortable time alone (HH:MM:SS)"
          label="Longest comfortable time alone (HH:MM:SS)"
          :error="!hasValidLongestAbsenceString"
        >
        </va-input>
        <va-input
          v-model="aloneGoalString"
          type="string"
          aria-label="Goal alone time (HH:MM:SS)"
          label="Goal alone time (HH:MM:SS)"
          :error="!hasValidGoalAloneString"
        >
        </va-input>
        <va-input
          v-if="!hasEmail"
          v-model="email"
          type="email"
          aria-label="Email"
          label="Email*"
        >
        </va-input>
      </div>
    </va-card>
    <va-card title="Today's exercise">
      <div v-if="displayTodaysMission">
        <div :style="{margin: '24px'}">
          Today's exercise should take approximately {{exerciseEstimate-5}}-{{exerciseEstimate+5}} minutes. Be sure to use a timer to track how long you're outside the home.
        </div>
        <va-item v-for="(step, index) in todaysMissionSteps" v-bind:key="index" >
          <va-item-section>
            <va-item-label>{{index + 1}}. {{step.label}}</va-item-label>
            <va-button @click="showTimer(index)" :disabled="buttonDisabled[index]"
              :style="{background: $themes.primary}">{{exerciseButtonText[index]}}</va-button>
          </va-item-section>
        </va-item>
        <div :style="{margin: '24px'}">
          <va-accordion>
            <va-collapse customHeader>
              <span slot="header">
                <va-button :outline="true" :color="$themes.gray"
                  :iconRight="`fa fa-angle-down va-collapse__header__icon`">
                  Rating details
                </va-button>
              </span>
              <div slot="body">
                <ol>
                  <li>1 - Agitated for most or all of the steps</li>
                  <li>2 - Mostly not relaxed but relaxed for a few steps</li>
                  <li>3 - Moved around a moderate amount, relaxed for some steps</li>
                  <li>4 - Some movement, relaxed for most steps</li>
                  <li>5 - Very little movement, fully relaxed for all steps</li>
                </ol>
              </div>
            </va-collapse>
          </va-accordion>
          <va-input
            v-model="todayRating"
            type="number"
            :max="5"
            :min="1"
            aria-label="Today's exercise rating"
            label="Today's exercise rating (1-5)"
            placeholder="5"
          >
          </va-input>
          <va-button @click="sendEmail">
            {{emailButtonText}}
          </va-button>
        </div>
      </div>
      <div v-else-if="displayBadEmail">
        Your email is not in our list. Please reach out to
        <a
          href="mailto:rewardingdogtrain@gmail.com"
          target="_blank"
          class="app-navbar__mailto-link"
          :style="{color: this.$themes.primary}"
        >
          rewardingdogtrain@gmail.com
        </a>
        to gain access to these exercises.
      </div>
      <div v-else>
        <div>If today is your first time or you need a refresher on what to do, please read the "Instructions" section above</div>
        <va-button :disabled="!validFormValues" @click="generateTodayMission">
          Generate today's exercise
        </va-button>
      </div>
    </va-card>
    <va-card v-if="email && !displayBadEmail" title="Feedback">
      <va-input
        v-model="feedbackMessage"
        type="textarea"
        label="Feedback message"
        :minRows="6"
        :style="{width: '100%'}"
      />
      <va-button :disabled="!feedbackMessage" @click="submitFeedback">
        {{feedbackButtonText}}
      </va-button>
    </va-card>
    <SeparationAnxietyFAQCard />
    <va-modal v-model="displayTimer" title="Rest timer"
      message="Between each step, please return to a place in your house and hang out, waiting approximately 1 to 2 minutes to let your dog settle before you begin with the next step. Feel free to calmly greet your dog and give them gentle pets. If you see that your dog is looking rather anxious you can take a longer pause up to 3 minutes."
      position="top" size="large" hide-default-actions>
      <div class="cancel-button" style="display: flex; justify-content: center;">
        <div v-if="timer">{{timer}}</div>
        <div v-else>
          <va-button flat color="gray" @click="closeTimer">
            Back to exercise
          </va-button>
        </div>
      </div>
    </va-modal>
    <va-modal v-model="displayEndOfExercise" title="Congrats"
      :message="`You just completed all steps for your training session today. Nice work! Make sure to give your dog plenty of rest time and press the '${emailButtonText}' button to get the link for your next training exercise emailed to you.`"
      position="top" size="large" hide-default-actions>
      <va-input
        v-model="todayRating"
        type="number"
        :max="5"
        :min="1"
        aria-label="Today's exercise rating"
        label="Today's exercise rating (1-5)"
        placeholder="5"
      >
      </va-input>
      <va-button @click="sendEmail">
        {{emailButtonText}}
      </va-button>
    </va-modal>
  </div>
</template>

<script>
import separationAnxeity from '../../data/separation-anxiety-warmups.json'
import SeparationAnxietyInstructionsCard from './SeparationAnxietyInstructionsCard.vue'
import SeparationAnxietyFAQCard from './SeparationAnxietyFAQCard.vue'
const moment = require('moment')

// The maximum and and the minimum is inclusive
function getRandomIntInclusive (min, max) {
  const minCeiled = Math.ceil(min)
  const maxFloored = Math.floor(max)
  return Math.floor(Math.random() * (maxFloored - minCeiled + 1) + minCeiled)
}

const allowedEmails = [
  'nadiamagallon@gmail.com',
  'rewardingdogtrain@gmail.com',
  'helenhong1224@gmail.com',
  'kslusser74@gmail.com',
  'rylanb@gmail.com',
  'autopro9966@outlook.com',
  'melpradit@gmail.com',
  'katiebontje@gmail.com',
  'smitley.lesa@gmail.com',
  'coreyzettler@gmail.com',
  'wo36@cornell.edu',
  'liss.randi@gmail.com',
  'cecilia.siyushen@gmail.com',
  'pmkingsmill@gmail.com',
  'jensfalcin@gmail.com',
  'clarygsawyer@gmail.com',
  'megrubes@gmail.com',
  'everhartlinds@gmail.com',
  'alex.easymagic12@gmail.com',
  'carriefulton00@yahoo.com',
]

export default {
  name: 'separation-anxiety-exercise-card',
  components: {
    SeparationAnxietyInstructionsCard,
    SeparationAnxietyFAQCard,
  },
  data () {
    return {
      selectModel: { id: 0, description: 'Day 1' },
      totalSteps: '',
      longestAbsenceString: '',
      aloneGoalString: '',
      longestAbsence: 0,
      email: '',
      feedbackMessage: '',
      lastRating: '',
      todayRating: '',
      lastStepDuration: '',
      todayWarmupStepsCount: 0,
      exerciseEstimate: 25,
      displayBadEmail: false,
      displayTodaysMission: false,
      displayTimer: false,
      timer: 0,
      displayEndOfExercise: false,
      buttonDisabled: [],
      exerciseButtonText: [],
      todaysMissionSteps: [],
      emailButtonText: 'Email your next exercise info',
      toastText: '',
      feedbackButtonText: 'Submit feedback',
      getQueryParams: function () {
        const urlParams = new URLSearchParams(window.location.search)
        const totalStepsParam = urlParams.get('totalSteps')
        const longestAbsenceParam = urlParams.get('longestAbsence')
        const emailParam = urlParams.get('email')
        const lastRatingParam = urlParams.get('lastRating')
        const goalAloneParam = urlParams.get('goalAlone')
        this.totalSteps = totalStepsParam || ''
        this.longestAbsence = longestAbsenceParam || 0
        this.email = emailParam || ''
        this.lastRating = lastRatingParam || ''
        this.goalAlone = goalAloneParam || ''

        return emailParam
      },
      timeInputValidation: function (input) {
        if (input) {
          const regex = new RegExp('([0-1][0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9])')
          return input && regex.test(input)
        }
        return true
      },
      todaysMissionStepsGenerator: function () {
        // if <30, only easy warmup steps
        // if <120, only easy & medium steps
        // if <600, only medium & hard steps
        // if >=600, sample from only hard
        // cap replacement of ZZZ to <= longest seconds / 3 or up to 60 (whichever is smaller)
        const warmupStepsEasy = separationAnxeity.warmups.easy
        const warmupStepsMedium = separationAnxeity.warmups.medium
        const warmupStepsHard = separationAnxeity.warmups.hard
        const warmupStepOptions = []
        if (this.lastStepDuration < 6) {
          warmupStepOptions.push(...warmupStepsEasy)
        } else if (this.lastStepDuration < 15) {
          warmupStepOptions.push(...warmupStepsEasy)
          warmupStepOptions.push(...warmupStepsMedium)
        } else if (this.lastStepDuration < 60) {
          warmupStepOptions.push(...warmupStepsEasy)
          warmupStepOptions.push(...warmupStepsMedium)
          warmupStepOptions.push(...warmupStepsMedium)
        } else if (this.lastStepDuration < 360) {
          warmupStepOptions.push(...warmupStepsEasy)
          warmupStepOptions.push(...warmupStepsMedium)
          warmupStepOptions.push(...warmupStepsMedium)
          warmupStepOptions.push(...warmupStepsHard)
          warmupStepOptions.push(...warmupStepsHard)
        } else if (this.lastStepDuration < 600) {
          warmupStepOptions.push(...warmupStepsMedium)
          warmupStepOptions.push(...warmupStepsHard)
        } else {
          warmupStepOptions.push(...warmupStepsHard)
        }
        const warmups = []
        this.buttonDisabled = [false]
        this.exerciseButtonText = ['Done']
        for (let x = 1; x < this.todayWarmupStepsCount + 1; x++) {
          const randomIndex = getRandomIntInclusive(0, warmupStepOptions.length - 1)
          const generatedWarmupSecondsMax = Math.floor(this.lastStepDuration / 3)
          const smallerSecondsUpperRange = generatedWarmupSecondsMax < 60 ? generatedWarmupSecondsMax : 59
          const randomSeconds = getRandomIntInclusive(1, smallerSecondsUpperRange)
          const stepValue = warmupStepOptions[randomIndex].replace('ZZZ', randomSeconds)
          warmups.push({ label: stepValue, value: false })
          this.buttonDisabled.push(true)
          this.exerciseButtonText.push('Todo')
        }

        return [...warmups, { label: this.finalStep.finalStepDescription, value: false }]
      },
      parseTimeStringIntoSeconds: (input) => {
        const parsedTimeString = input.trim().split(':')
        const [hours, minutes, seconds] = parsedTimeString

        return (3600 * Number(hours)) + (60 * Number(minutes)) + Number(seconds)
      },
      calculateLongestAbsenceSeconds: () => {
        this.longestAbsence = this.parseTimeStringIntoSeconds(this.longestAbsenceString)
      },
      generateTodayMission: () => {
        // Check if email is allowed
        if (!this.email || !allowedEmails.includes(this.email)) {
          this.displayBadEmail = true
          return
        }

        this.displayBadEmail = false

        // Warmup steps
        const origWarmupStepsCount = Number(this.totalSteps) ? Number(this.totalSteps) - 1 : 10

        const randomProbability = Math.random()
        const warmupStepsCount = (randomProbability < 0.4) ? origWarmupStepsCount - 1 : (randomProbability < 0.7) ? origWarmupStepsCount : origWarmupStepsCount + 1
        if (this.longestAbsence < 60 && warmupStepsCount < 7) {
          this.todayWarmupStepsCount = 7
        } else if (this.longestAbsence < 1800 && warmupStepsCount < 2) {
          this.todayWarmupStepsCount = 2
        } else if (warmupStepsCount > 11) {
          this.todayWarmupStepsCount = 11
        } else {
          this.todayWarmupStepsCount = warmupStepsCount
        }

        // Max alone time
        if (this.longestAbsenceString) {
          this.calculateLongestAbsenceSeconds()
        }
        const origLongestAbsence = this.longestAbsence || 3 // stored in seconds
        // scale up/down the longest absence value
        const randomProbabilityForAbsence = Math.random()
        let nextDuration = origLongestAbsence
        // If last exercise had low rating, drop final step duration to be between 30-65% of yesterdays duration
        if (this.lastRating && this.lastRating < 3) {
          nextDuration = (randomProbabilityForAbsence < 0.65 && randomProbabilityForAbsence > 0.3) ? randomProbabilityForAbsence * origLongestAbsence : 0.65 * origLongestAbsence
        } else {
          if (origLongestAbsence < 15) {
            nextDuration = (randomProbabilityForAbsence < 0.8) ? (1 + randomProbabilityForAbsence) * origLongestAbsence : randomProbabilityForAbsence * origLongestAbsence
          } else {
            nextDuration = (randomProbabilityForAbsence < 0.4) ? (1 + randomProbabilityForAbsence) * origLongestAbsence : randomProbabilityForAbsence * origLongestAbsence
          }
        }
        this.lastStepDuration = Math.round(nextDuration)
        if (this.lastStepDuration < 2) {
          this.lastStepDuration = 1
        }

        // Create Mission steps & info
        this.exerciseEstimate = Math.floor(2.3 * this.todayWarmupStepsCount) + Math.floor(this.lastStepDuration / 60)
        this.todaysMissionSteps = this.todaysMissionStepsGenerator()

        // Display the mission
        this.displayTodaysMission = true
      },
      disableButton: (index) => {
        this.buttonDisabled[index] = true
        this.buttonDisabled[index + 1] = false

        this.exerciseButtonText[index + 1] = 'Done'
      },
      runTimer: async () => {
        if (this.timer > 0) {
          setTimeout(() => {
            this.timer -= 1
            this.runTimer()
          }, 1000)
        }
        return this.timer
      },
      showTimer: (index) => {
        this.disableButton(index)
        if (index < this.todayWarmupStepsCount) {
          this.timer = getRandomIntInclusive(60, 120)
          this.displayEndOfExercise = false
          this.displayTimer = true
          this.runTimer()
        } else {
          this.displayTimer = false
          this.displayEndOfExercise = true
        }
      },
      closeTimer: () => {
        this.displayTimer = false
      },
      sendEmail: () => {
        this.emailButtonText = 'Sending...'

        const windowLocation = window.location
        const baseURL = `${windowLocation.origin}${windowLocation.pathname}`
        let params = `?email=${this.email}&totalSteps=${this.todayWarmupStepsCount + 1}&longestAbsence=${this.lastStepDuration}`

        if (this.todayRating) {
          params = `${params}&lastRating=${this.todayRating}`
        }
        if (this.aloneGoalSeconds) {
          params = `${params}&goalAlone=${this.aloneGoalSeconds}`
        }

        const generatedURL = `${baseURL}${params}`
        const message = `Congrats on completing a separation anxiety training exercise today!\n\nYou successfully left your dog alone for ${this.finalStep.lastStepDurationString}.\n\nYour next exercise can be found by clicking the following link: ${generatedURL}`
        const reminderMessage = `Nice work on completing your last separation anxiety training exercise. \n\nAs a reminder, your next exercise can be found by clicking the following link: ${generatedURL} \n\n If you've already completed the exercise, you can disregard this email.`

        console.log(generatedURL)

        const tomorrowReminderTime = moment().add(1, 'days').hours(8).startOf('hour')
        const secondsTillTomorrowReminderUnixTimestamp = tomorrowReminderTime.unix()

        // Close any open modals
        this.displayEndOfExercise = false
        this.displayTimer = false

        const mail = new FormData()
        mail.append('email', this.email)
        mail.append('message', message)
        mail.append('sendAtTimestamp', secondsTillTomorrowReminderUnixTimestamp)
        mail.append('reminderMessage', reminderMessage)
        mail.append('subject', 'Separation Anxiety Exercise')
        this.sendMail(mail)
      },
      sendMail (mail) {
        fetch('https://rewarding-dog-training-email.herokuapp.com/sendSeparationAnxietyEmail', {
          method: 'post',
          body: mail,
        }).then((response) => {
          if (response.status === 500) {
            this.toastText = 'An error occured when sending this message'
          } else if (response.status === 200) {
            this.toastText = 'Message Sent'
            this.$emit('sent-request')
          } else {
            this.toastText = 'Please try again'
          }
          this.emailButtonText = 'Email your next exercise info'
          this.launchToast()
        })
          .catch((err) => {
            console.log(err)
            this.toastText = 'An error occured when sending this message'
            this.emailButtonText = 'Email your next exercise info'
            this.launchToast()
          })
      },
      launchToast () {
        this.showToast(
          this.toastText,
          {
            position: 'top-center',
            duration: 4500,
            fullWidth: false,
          },
        )
      },
      sendFeedbackMail (mail) {
        fetch('https://rewarding-dog-training-email.herokuapp.com/sendEmail', {
          method: 'post',
          body: mail,
        }).then((response) => {
          if (response.status === 500) {
            this.toastText = 'An error occured when sending this message'
          } else if (response.status === 200) {
            this.toastText = 'Message Sent'
            this.$emit('sent-request')
          } else {
            this.toastText = 'Please try again'
          }
          this.feedbackButtonText = 'Submit feedback'
          this.launchToast()
        })
          .catch((err) => {
            console.log(err)
            this.toastText = 'An error occured when sending this message'
            this.launchToast()
            this.feedbackButtonText = 'Submit feedback'
          })
      },
      submitFeedback: () => {
        this.feedbackButtonText = 'Sending...'

        const mail = new FormData()
        mail.append('name', 'Feedback from:')
        mail.append('email', this.email)
        mail.append('message', this.feedbackMessage)
        mail.append('subject', 'Feedback - Separation Anxiety Exercise')
        // mail.append('cc', ['helenhong1224@gmail.com', 'kslusser74@gmail.com'])

        this.sendFeedbackMail(mail)
      },
    }
  },
  computed: {
    aloneGoalSeconds: function () {
      if (this.aloneGoalString && this.hasValidGoalAloneString) {
        return this.parseTimeStringIntoSeconds(this.aloneGoalString)
      }
      return 0
    },
    hasEmail: function () {
      return Boolean(this.getQueryParams())
    },
    hasValidLongestAbsenceString: function () {
      return this.timeInputValidation(this.longestAbsenceString)
    },
    hasValidGoalAloneString: function () {
      return this.timeInputValidation(this.aloneGoalString)
    },
    validFormValues: function () {
      return this.email && this.timeInputValidation(this.longestAbsenceString) && this.timeInputValidation(this.aloneGoalString)
    },
    finalStep: function () {
      let totalSeconds = this.lastStepDuration
      const hours = Math.floor(totalSeconds / 3600)
      totalSeconds -= (hours * 3600)
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = totalSeconds % 60
      let lastStepDurationString = ''
      if (hours) {
        lastStepDurationString = lastStepDurationString.concat(`${hours} hours`)
      }
      if (minutes) {
        lastStepDurationString = lastStepDurationString.concat(` ${minutes} minutes`)
      }
      if (!hours && seconds) {
        lastStepDurationString = lastStepDurationString.concat(` ${seconds} seconds`)
      }
      const finalStepDescription = separationAnxeity.finalStep.replace('ZZZ', lastStepDurationString.trim())
      return { finalStepDescription, lastStepDurationString }
    },
  },
}
</script>

<style lang="scss">
  .va-card {
    &__body {
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      .exercise-customizations {
        display: flex;
        flex-direction: column;
        justify-content: left;
        width: 100%;
      }

      .va-item {
        width: 100%;

        &-label {
          overflow: unset;
          white-space: break-spaces;
          text-align: left;
        }
      }

      ul {
        margin-left: 2rem;

        li {
          list-style: circle;

          .italic {
            margin-top: 0;
            font-style: italic;
          }
        }
      }
    }
  }
</style>

<!-- TODO: Add longest ever absence, adjust probabilities based on longest ever. Celebrate when hit a new longest milestone  -->
